'use client';

import Link from 'next/link';
import { useState, useEffect } from 'react';
import {
  Menu, X, Search, Info, Home, Newspaper, Mail as MailIcon, Heart, MessageSquare, User, LogOut,
  ExternalLink, ChevronRight, Instagram, Linkedin,
} from 'lucide-react';
import { PageType, PAGE_PATHS, HUSTLANCE_URL, HUSTLANCE_INSTAGRAM_URL, HUSTLANCE_REDDIT_URL, HUSTLANCE_DISCORD_URL, HUSTLANCE_LINKEDIN_URL } from './Router';
import { useAuth } from './AuthContext';
import { LoginModal } from './LoginModal';
import { RedditIcon, DiscordIcon } from './icons/BrandIcons';
import { ImageWithFallback } from './figma/ImageWithFallback';
import logoImport from '../assets/hustlance-logo.webp';

// Next.js's image imports return a { src, width, height } object rather
// than a plain URL string — normalize so a plain <img src> works.
const logoSrc = typeof logoImport === 'string' ? logoImport : (logoImport as { src: string }).src;
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

interface HeaderProps {
  onNavigate?: (page: PageType) => void;
  // Accepted for drop-in compatibility with page components ported over from
  // the main Hustlance app's Header prop signature — unused here, this site
  // has no /plans page.
  onNavigateToPlans?: () => void;
}

export function Header({ onNavigate }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const closeMobile = () => setIsMobileMenuOpen(false);

  const navLinks = [
    { label: 'Home', page: 'blog-home' as PageType, icon: Home },
    { label: 'All News', page: 'blog-all-news' as PageType, icon: Newspaper },
    { label: 'About', page: 'blog-about' as PageType, icon: Info },
    { label: 'Contact', page: 'blog-contact' as PageType, icon: MailIcon },
  ];

  const socialLinks = [
    { href: HUSTLANCE_INSTAGRAM_URL, label: 'Hustlance on Instagram', icon: Instagram },
    { href: HUSTLANCE_REDDIT_URL, label: 'Hustlance on Reddit', icon: RedditIcon },
    { href: HUSTLANCE_DISCORD_URL, label: 'Hustlance on Discord', icon: DiscordIcon },
    { href: HUSTLANCE_LINKEDIN_URL, label: 'Hustlance on LinkedIn', icon: Linkedin },
  ];

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-hustlance-primary shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-14 gap-2">
            <Link href={PAGE_PATHS['blog-home']} aria-label="Hustlance Blog home" className="flex items-center flex-shrink-0 mr-2">
              <ImageWithFallback
                src={logoSrc}
                alt="Hustlance"
                width={32}
                height={32}
                className="h-8 w-8 rounded-lg object-cover"
              />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map(link => (
                <Link
                  key={link.page}
                  href={PAGE_PATHS[link.page]}
                  className="text-white/90 hover:text-white font-bold text-sm px-3 py-2 rounded transition-colors hover:bg-white/10"
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Search icon (desktop, centered via ml-auto/mr-auto trick) */}
            <Link
              href={PAGE_PATHS['blog-search']}
              aria-label="Search"
              className="hidden lg:flex items-center justify-center text-white/90 hover:text-white p-2 rounded hover:bg-white/10 transition-colors mx-auto"
            >
              <Search className="w-5 h-5" />
            </Link>

            {/* Desktop right side */}
            <div className="hidden lg:flex items-center gap-2">
              <div className="flex items-center gap-1 mr-1 pr-2 border-r border-white/20">
                {socialLinks.map(({ href, label, icon: Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="text-white/80 hover:text-white p-1.5 rounded hover:bg-white/10 transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
              <a
                href={HUSTLANCE_URL}
                className="flex items-center gap-1.5 text-sm text-white/80 hover:text-white transition-colors font-medium px-3 py-2 rounded hover:bg-white/10"
              >
                Hustlance <ExternalLink className="w-3.5 h-3.5" />
              </a>

              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 hover:bg-white/10 rounded-lg pl-2 pr-3 py-1.5 transition-colors">
                      <Avatar className="h-7 w-7">
                        <AvatarImage src={user?.profileImage} alt={user?.name} />
                        <AvatarFallback className="bg-white/20 text-white text-xs">
                          {user?.name?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-semibold text-white">{user?.name}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52 mt-1">
                    <DropdownMenuItem onClick={() => onNavigate?.('blog-account')}>
                      <User className="mr-2 h-4 w-4" /> My Account
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onNavigate?.('blog-account-liked')}>
                      <Heart className="mr-2 h-4 w-4" /> Liked Articles
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onNavigate?.('blog-account-comments')}>
                      <MessageSquare className="mr-2 h-4 w-4" /> My Comments
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600">
                      <LogOut className="mr-2 h-4 w-4" /> Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="bg-white text-hustlance-primary px-4 py-1.5 rounded text-sm font-bold hover:bg-white/90 transition-colors"
                >
                  Sign In
                </button>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden ml-auto p-2 rounded-lg hover:bg-white/10 transition-colors relative z-50"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeMobile} />
        <div
          className={`absolute right-0 top-0 bottom-0 w-80 max-w-full bg-white shadow-2xl transition-transform duration-300 flex flex-col ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-hustlance-primary">
            <div className="flex items-center gap-2.5">
              <ImageWithFallback src={logoSrc} alt="Hustlance" width={28} height={28} className="h-7 w-7 rounded-lg object-cover" />
              <span className="font-blocky text-white text-base">Hustlance Blog</span>
            </div>
            <button onClick={closeMobile} aria-label="Close menu" className="p-2 rounded-lg hover:bg-white/10 transition-colors">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {isAuthenticated && (
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-3">
                <Avatar className="h-11 w-11 border-2 border-white shadow">
                  <AvatarImage src={user?.profileImage} alt={user?.name} />
                  <AvatarFallback className="bg-hustlance-primary text-white">
                    {user?.name?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <p className="font-semibold text-gray-900">{user?.name}</p>
              </div>
            </div>
          )}

          <nav className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
            {[...navLinks, { label: 'Search', page: 'blog-search' as PageType, icon: Search }].map(link => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.page}
                  href={PAGE_PATHS[link.page]}
                  onClick={closeMobile}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-gray-700 hover:bg-gray-50 hover:text-hustlance-primary transition-all group"
                >
                  <Icon className="w-5 h-5 text-gray-400 group-hover:text-hustlance-primary transition-colors" />
                  <span className="font-medium">{link.label}</span>
                  <ChevronRight className="w-4 h-4 ml-auto text-gray-300" />
                </Link>
              );
            })}

            {isAuthenticated && (
              <div className="border-t border-gray-100 pt-2 mt-2 space-y-1">
                <button
                  onClick={() => { onNavigate?.('blog-account'); closeMobile(); }}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-gray-700 hover:bg-gray-50 transition-all"
                >
                  <User className="w-5 h-5 text-gray-400" /> <span className="font-medium">My Account</span>
                </button>
                <button
                  onClick={() => { onNavigate?.('blog-account-liked'); closeMobile(); }}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-gray-700 hover:bg-gray-50 transition-all"
                >
                  <Heart className="w-5 h-5 text-gray-400" /> <span className="font-medium">Liked Articles</span>
                </button>
                <button
                  onClick={() => { onNavigate?.('blog-account-comments'); closeMobile(); }}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-gray-700 hover:bg-gray-50 transition-all"
                >
                  <MessageSquare className="w-5 h-5 text-gray-400" /> <span className="font-medium">My Comments</span>
                </button>
              </div>
            )}
          </nav>

          <div className="p-4 border-t border-gray-100 space-y-2.5">
            <div className="flex items-center justify-center gap-4 py-2">
              {socialLinks.map(({ href, label, icon: Icon }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="text-gray-400 hover:text-hustlance-primary transition-colors">
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
            <a
              href={HUSTLANCE_URL}
              className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl text-gray-700 font-semibold border border-gray-200 hover:bg-gray-50 transition-all"
            >
              Hustlance <ExternalLink className="w-4 h-4" />
            </a>
            {isAuthenticated ? (
              <button
                onClick={() => { logout(); closeMobile(); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl text-red-600 font-semibold border border-red-100 hover:bg-red-50 transition-all"
              >
                <LogOut className="w-4 h-4" /> Sign Out
              </button>
            ) : (
              <button
                onClick={() => { setShowLoginModal(true); closeMobile(); }}
                className="w-full px-4 py-3.5 rounded-xl bg-hustlance-primary text-white font-bold hover:opacity-90 transition-all"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </div>

      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} onNavigate={onNavigate as (page: string) => void} />
    </>
  );
}
